// Группировка уведомлений по временным секциям (Сегодня / Вчера / На этой
// неделе / Ранее). Используется центром уведомлений для замены плоского списка
// на сканируемые секции с заголовками — тактильный редизайн «Картотека» §8.V.

import type { NotificationItemData } from "./notification-item";

/** Ключ временной секции. i18n-ключ — `groups.{key}`. */
export type DateGroupKey = "today" | "yesterday" | "thisWeek" | "earlier";

export interface DateGroup {
	key: DateGroupKey;
	items: NotificationItemData[];
}

const GROUP_ORDER: readonly DateGroupKey[] = ["today", "yesterday", "thisWeek", "earlier"];

/** Начало календарного дня (локальная зона) для переданной даты. */
function startOfDay(d: Date): Date {
	const copy = new Date(d);
	copy.setHours(0, 0, 0, 0);
	return copy;
}

/** Определяет секцию для одного уведомления относительно `now`. */
function resolveGroup(createdAt: string, now: Date): DateGroupKey {
	const created = new Date(createdAt);
	const todayStart = startOfDay(now);
	const dayMs = 86_400_000;

	if (created.getTime() >= todayStart.getTime()) return "today";
	if (created.getTime() >= todayStart.getTime() - dayMs) return "yesterday";
	// «На этой неделе» — последние 7 дней (но не сегодня/вчера).
	if (created.getTime() >= todayStart.getTime() - 7 * dayMs) return "thisWeek";
	return "earlier";
}

/**
 * Раскладывает уведомления по временным секциям, сохраняя исходный порядок
 * (новые сверху) внутри каждой секции. Пустые секции отбрасываются.
 */
export function groupByDate(items: NotificationItemData[], now: Date = new Date()): DateGroup[] {
	const buckets = new Map<DateGroupKey, NotificationItemData[]>();
	for (const item of items) {
		const key = resolveGroup(item.createdAt, now);
		const bucket = buckets.get(key);
		if (bucket) bucket.push(item);
		else buckets.set(key, [item]);
	}
	return GROUP_ORDER.filter((key) => buckets.has(key)).map((key) => ({
		key,
		items: buckets.get(key) ?? [],
	}));
}
